import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  FormHelperText,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';

import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

interface Expert {
  id: number;
  user_id: number | null;
  first_name: string;
  middle_name?: string;
  last_name: string;
  job_title: string;
}

interface RetainerFormProps {
  caseId: number;
  caseName: string;
  isRush: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const RetainerForm: React.FC<RetainerFormProps> = ({
  caseId,
  caseName,
  isRush: _isRush,
  onSuccess,
  onCancel,
}) => {
  const [experts, setExperts] = useState<Expert[]>([]);
  const [selectedExpert, setSelectedExpert] = useState<number | ''>('');
  const [retainerFormFile, setRetainerFormFile] = useState<File | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceStatus, setInvoiceStatus] = useState<'paid' | 'unpaid'>(
    'unpaid'
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchExperts();
  }, []);

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/experts');
      setExperts(response.data.experts || []);
    } catch (error) {
      console.error('Failed to fetch experts:', error);
      toast.error('Failed to load experts');
      setExperts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleRetainerFormFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          'Please select a PDF or Word document for the retainer form'
        );
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Retainer form file must be less than 10MB');
        return;
      }
      setRetainerFormFile(file);
      setErrors(prev => ({ ...prev, retainerFormFile: '' }));
    }
  };

  const handleInvoiceFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          'Please select a PDF, Word document, or image for the invoice'
        );
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Invoice file must be less than 10MB');
        return;
      }
      setInvoiceFile(file);
      setErrors(prev => ({ ...prev, invoiceFile: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!retainerFormFile) {
      newErrors.retainerFormFile = 'Retainer form file is required';
    }

    if (!selectedExpert) {
      newErrors.experts = 'An expert must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('retainer_form_file', retainerFormFile!);
      if (invoiceFile) {
        formData.append('invoice_file', invoiceFile);
        formData.append('invoice_status', invoiceStatus);
      }
      formData.append('expert_id', selectedExpert.toString());

      await api.post(`/cases/${caseId}/submit-retainer-form`, formData);

      toast.success('Retainer form submitted successfully!');
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to submit retainer form:', error);
      toast.error(
        error.response?.data?.message || 'Failed to submit retainer form'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getExpertName = (expert: Expert) => {
    let name = expert.first_name;
    if (expert.middle_name) {
      name += ` ${expert.middle_name}`;
    }
    name += ` ${expert.last_name}`;
    return name;
  };

  return (
    <Card>
      <CardHeader
        title='Retainer Form Submission'
        subheader={`Case: ${caseName}`}
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Retainer Form File Upload */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='h6' gutterBottom>
              Retainer Form File
            </Typography>
            <input
              accept='.pdf,.doc,.docx'
              style={{ display: 'none' }}
              id='retainer-form-file'
              type='file'
              onChange={handleRetainerFormFileChange}
            />
            <label htmlFor='retainer-form-file'>
              <Button
                variant='outlined'
                component='span'
                startIcon={<CloudUploadIcon />}
                disabled={submitting}
              >
                Upload Retainer Form
              </Button>
            </label>
            {retainerFormFile && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color='success' sx={{ mr: 1 }} />
                <Typography variant='body2'>{retainerFormFile.name}</Typography>
                <IconButton
                  size='small'
                  onClick={() => setRetainerFormFile(null)}
                  disabled={submitting}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            {errors.retainerFormFile && (
              <FormHelperText error>{errors.retainerFormFile}</FormHelperText>
            )}
          </Paper>

          {/* Expert Selection */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='h6' gutterBottom>
              Assign Expert
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Select an expert to assign to this case
            </Typography>
            {loading ? (
              <CircularProgress size={20} />
            ) : experts && experts.length > 0 ? (
              <FormControl fullWidth error={!!errors.experts}>
                <InputLabel>Expert</InputLabel>
                <Select
                  value={selectedExpert}
                  label='Expert'
                  onChange={e => setSelectedExpert(e.target.value as number)}
                  disabled={submitting}
                >
                  {experts
                    .filter(expert => expert.user_id !== null)
                    .map(expert => (
                      <MenuItem key={expert.user_id!} value={expert.user_id!}>
                        {getExpertName(expert)} - {expert.job_title}
                      </MenuItem>
                    ))}
                </Select>
                {errors.experts && (
                  <FormHelperText error>{errors.experts}</FormHelperText>
                )}
              </FormControl>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No experts available. Please contact an administrator.
              </Typography>
            )}
          </Paper>

          {/* Invoice Section */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='h6' gutterBottom>
              Invoice Information
            </Typography>

            {/* Invoice File Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle1' gutterBottom>
                Invoice File (optional)
              </Typography>
              <input
                accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                style={{ display: 'none' }}
                id='invoice-file'
                type='file'
                onChange={handleInvoiceFileChange}
              />
              <label htmlFor='invoice-file'>
                <Button
                  variant='outlined'
                  component='span'
                  startIcon={<CloudUploadIcon />}
                  disabled={submitting}
                >
                  Upload Invoice
                </Button>
              </label>
              {invoiceFile && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon color='success' sx={{ mr: 1 }} />
                  <Typography variant='body2'>{invoiceFile.name}</Typography>
                  <IconButton
                    size='small'
                    onClick={() => setInvoiceFile(null)}
                    disabled={submitting}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
              {errors.invoiceFile && (
                <FormHelperText error>{errors.invoiceFile}</FormHelperText>
              )}
            </Box>

            {/* Invoice Status */}
            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Invoice Status
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={invoiceStatus}
                  onChange={e =>
                    setInvoiceStatus(e.target.value as 'paid' | 'unpaid')
                  }
                  disabled={submitting}
                >
                  <MenuItem value='unpaid'>Unpaid</MenuItem>
                  <MenuItem value='paid'>Paid</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant='outlined'
              onClick={onCancel}
              disabled={submitting}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} />
                ) : (
                  <CheckCircleIcon />
                )
              }
            >
              {submitting ? 'Submitting...' : 'Submit Retainer Form'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RetainerForm;
